/**
 * Planes (dentro del área de Licencias): gestión de products (entidades
 * comerciales con ciclo de vida). Precio/moneda/intervalo son INMUTABLES cuando
 * el plan está en uso (≥1 suscripción o pago offline): la UI los bloquea con
 * tooltip y ofrece "Crear nueva versión" (duplicar). Desactivar no afecta
 * suscripciones existentes, solo impide seleccionar el plan en checkouts/pagos
 * nuevos. Nunca hay borrado físico. El `codigo` lo genera el backend.
 */
import { useState } from "react";
import { adminApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type { Audiencia, IntervaloPlan, Modalidad, ProductoAdmin } from "../../api/types.js";
import { Async, ConfirmDialog, fmtFechaCorta } from "./ui.js";
import { VolverLicencias } from "./AdminPagosOffline.js";
import s from "./admin.module.css";

const INTERVALOS: IntervaloPlan[] = ["mensual", "anual"];

/** Muestra el cupo de evaluaciones; "∞" si ilimitado (null). */
function fmtCreditos(n: number | null): string {
  return n === null ? "∞" : String(n);
}
/** Campo de texto → cupo: vacío = ilimitado (null); si no, entero. */
function parseCreditos(v: string): number | null {
  const t = v.trim();
  return t === "" ? null : Number(t);
}

function ActivoBadge({ activo }: { activo: boolean }) {
  return activo ? (
    <span className={`${s.badge} ${s.badgeGreen}`}>Activo</span>
  ) : (
    <span className={`${s.badge} ${s.badgeNeutral}`}>Inactivo</span>
  );
}

export function AdminPlanes() {
  const q = useQuery(() => adminApi.planes(), []);
  const [crear, setCrear] = useState(false);
  const [editar, setEditar] = useState<ProductoAdmin | null>(null);
  const [versionar, setVersionar] = useState<ProductoAdmin | null>(null);
  const [toggle, setToggle] = useState<ProductoAdmin | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function recargar() {
    setCrear(false);
    setEditar(null);
    setVersionar(null);
    setToggle(null);
    q.reload();
  }

  async function confirmarToggle() {
    if (!toggle) return;
    setOcupado(true);
    setError(null);
    try {
      await adminApi.editarPlan(toggle.id, { activo: !toggle.activo });
      recargar();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      <VolverLicencias />
      <div className={s.toolbar} style={{ marginBottom: 4 }}>
        <div className={s.sectionTitle}>Paquetes</div>
        <span className={s.headerSpacer} />
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => setCrear(true)}>
          Crear paquete
        </button>
      </div>

      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}

      <div className={s.panel}>
        <Async q={q}>
          {(planes) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Audiencia</th>
                    <th>Precio</th>
                    <th>Modalidad</th>
                    <th>Evaluaciones</th>
                    <th>Estado</th>
                    <th>Ventas</th>
                    <th>Creado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {planes.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.nombre}
                        {p.descripcion ? <div className={s.muted}>{p.descripcion}</div> : null}
                        {p.enUso ? (
                          <span className={`${s.badge} ${s.badgeBlue}`} style={{ marginTop: 4 }} title="Precio/intervalo bloqueados por uso">
                            en uso
                          </span>
                        ) : null}
                      </td>
                      <td className={s.muted}>{p.audiencia}</td>
                      <td>
                        {p.precio.toFixed(2)} {p.moneda}
                      </td>
                      <td className={s.muted}>
                        {p.modalidad === "suscripcion"
                          ? p.intervalo
                          : `paquete${p.vigenciaDias ? ` · ${p.vigenciaDias}d` : " · ∞"}`}
                      </td>
                      <td>{fmtCreditos(p.evaluacionesIncluidas)}</td>
                      <td>
                        <ActivoBadge activo={p.activo} />
                      </td>
                      <td className={s.muted}>
                        {p.suscripcionesActivas}
                        {p.pagosOffline > 0 ? (
                          <span className={s.muted}> · {p.pagosOffline} offline</span>
                        ) : null}
                      </td>
                      <td className={s.muted}>{fmtFechaCorta(p.createdAt)}</td>
                      <td>
                        <div className={s.btnRow}>
                          <button className={s.btn} onClick={() => setEditar(p)}>
                            Editar
                          </button>
                          <button className={s.btn} onClick={() => setVersionar(p)}>
                            Nueva versión
                          </button>
                          <button
                            className={`${s.btn} ${p.activo ? s.btnDanger : ""}`}
                            onClick={() => setToggle(p)}
                          >
                            {p.activo ? "Desactivar" : "Activar"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {planes.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={s.empty}>
                        Aún no hay paquetes. Crea el primero con “Crear paquete”.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      {crear ? <CrearModal onHecho={recargar} onCerrar={() => setCrear(false)} /> : null}
      {editar ? <EditarModal plan={editar} onHecho={recargar} onCerrar={() => setEditar(null)} /> : null}
      {versionar ? (
        <VersionarModal plan={versionar} onHecho={recargar} onCerrar={() => setVersionar(null)} />
      ) : null}
      {toggle ? (
        <ConfirmDialog
          titulo={toggle.activo ? "Desactivar paquete" : "Activar paquete"}
          cuerpo={
            toggle.activo ? (
              <>
                Desactivar “{toggle.nombre}”. No afecta a las suscripciones existentes; solo deja de
                aparecer en checkouts y pagos offline nuevos.
              </>
            ) : (
              <>Activar “{toggle.nombre}”: volverá a estar disponible para seleccionar.</>
            )
          }
          confirmar={toggle.activo ? "Desactivar" : "Activar"}
          peligro={toggle.activo}
          ocupado={ocupado}
          onConfirm={() => void confirmarToggle()}
          onCancel={() => setToggle(null)}
        />
      ) : null}
    </>
  );
}

// ---- modal genérico -------------------------------------------------------
function Modal({
  titulo,
  children,
  onCerrar,
}: {
  titulo: string;
  children: React.ReactNode;
  onCerrar: () => void;
}) {
  return (
    <div className={s.overlay} role="dialog" aria-modal="true" onClick={onCerrar}>
      <div className={s.modal} onClick={(e) => e.stopPropagation()}>
        <div className={s.modalTitle}>{titulo}</div>
        <div className={s.modalBody}>{children}</div>
      </div>
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "block", marginBottom: 8 }}>
      <div className={s.dt}>{label}</div>
      {children}
    </label>
  );
}

function CrearModal({ onHecho, onCerrar }: { onHecho: () => void; onCerrar: () => void }) {
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [audiencia, setAudiencia] = useState<Audiencia>("terapeuta");
  const [precio, setPrecio] = useState("");
  const [moneda, setMoneda] = useState("PEN");
  const [creditos, setCreditos] = useState("");
  const [vigencia, setVigencia] = useState("365");
  const [activo, setActivo] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar() {
    setError(null);
    if (!nombre.trim()) return setError("El nombre es obligatorio.");
    if (!(Number(precio) >= 0)) return setError("Precio inválido.");
    setOcupado(true);
    try {
      // Modelo unificado: todo producto nuevo es un PAQUETE (compra única).
      await adminApi.crearPlan({
        nombre: nombre.trim(),
        ...(descripcion.trim() ? { descripcion: descripcion.trim() } : {}),
        audiencia,
        modalidad: "paquete",
        precio: Number(precio),
        moneda: moneda.trim() || "PEN",
        intervalo: null,
        evaluacionesIncluidas: parseCreditos(creditos),
        vigenciaDias: vigencia.trim() === "" ? null : Number(vigencia),
        activo,
      });
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal titulo="Crear paquete" onCerrar={onCerrar}>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <Campo label="Nombre">
        <input className={s.search} style={{ width: "100%" }} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Campo>
      <Campo label="Descripción (opcional)">
        <input className={s.search} style={{ width: "100%" }} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Campo>
      <Campo label="Audiencia">
        <select className={s.select} value={audiencia} onChange={(e) => setAudiencia(e.target.value as Audiencia)}>
          <option value="terapeuta">Terapeuta</option>
          <option value="padre">Padre</option>
        </select>
      </Campo>
      <Campo label="Precio">
        <input className={s.search} type="number" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} />
      </Campo>
      <Campo label="Moneda">
        <input className={s.search} maxLength={3} value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} />
      </Campo>
      <Campo label="Evaluaciones del paquete (vacío = ilimitado)">
        <input
          className={s.search}
          type="number"
          min={0}
          placeholder="∞"
          value={creditos}
          onChange={(e) => setCreditos(e.target.value)}
        />
      </Campo>
      <Campo label="Vigencia en días (vacío = no expira)">
        <input className={s.search} type="number" min={1} placeholder="365" value={vigencia} onChange={(e) => setVigencia(e.target.value)} />
      </Campo>
      <label className={s.count} style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
        <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} /> Activo
      </label>
      <div className={s.modalActions}>
        <button className={s.btn} onClick={onCerrar} disabled={ocupado}>
          Cancelar
        </button>
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => void enviar()} disabled={ocupado}>
          {ocupado ? "…" : "Crear"}
        </button>
      </div>
    </Modal>
  );
}

function EditarModal({
  plan,
  onHecho,
  onCerrar,
}: {
  plan: ProductoAdmin;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(plan.nombre);
  const [descripcion, setDescripcion] = useState(plan.descripcion ?? "");
  const [activo, setActivo] = useState(plan.activo);
  const [precio, setPrecio] = useState(String(plan.precio));
  const [moneda, setMoneda] = useState(plan.moneda);
  const [intervalo, setIntervalo] = useState<IntervaloPlan>(plan.intervalo ?? "mensual");
  const [creditos, setCreditos] = useState(plan.evaluacionesIncluidas === null ? "" : String(plan.evaluacionesIncluidas));
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bloqueado = plan.enUso; // precio/moneda/intervalo/evaluaciones inmutables

  async function enviar() {
    setError(null);
    if (!nombre.trim()) return setError("El nombre es obligatorio.");
    setOcupado(true);
    try {
      await adminApi.editarPlan(plan.id, {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        activo,
        ...(bloqueado
          ? {}
          : {
              precio: Number(precio),
              moneda: moneda.trim() || "PEN",
              intervalo,
              evaluacionesIncluidas: parseCreditos(creditos),
            }),
      });
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  const tooltip = "Bloqueado: el plan ya tiene suscripciones o pagos. Usa “Nueva versión” para cambiar el precio.";

  return (
    <Modal titulo={`Editar · ${plan.nombre}`} onCerrar={onCerrar}>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      {bloqueado ? (
        <div className={`${s.banner} ${s.bannerInfo}`}>
          Precio, moneda, intervalo y evaluaciones incluidas están bloqueados: el plan ya está en uso.
          Para cambiarlos, usa <strong>“Nueva versión”</strong>.
        </div>
      ) : null}
      <Campo label="Nombre">
        <input className={s.search} style={{ width: "100%" }} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Campo>
      <Campo label="Descripción">
        <input className={s.search} style={{ width: "100%" }} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Campo>
      <Campo label="Precio">
        <input
          className={s.search}
          type="number"
          step="0.01"
          value={precio}
          disabled={bloqueado}
          title={bloqueado ? tooltip : undefined}
          onChange={(e) => setPrecio(e.target.value)}
        />
      </Campo>
      <Campo label="Moneda">
        <input
          className={s.search}
          maxLength={3}
          value={moneda}
          disabled={bloqueado}
          title={bloqueado ? tooltip : undefined}
          onChange={(e) => setMoneda(e.target.value.toUpperCase())}
        />
      </Campo>
      <Campo label="Intervalo">
        <select
          className={s.select}
          value={intervalo}
          disabled={bloqueado}
          title={bloqueado ? tooltip : undefined}
          onChange={(e) => setIntervalo(e.target.value as IntervaloPlan)}
        >
          {INTERVALOS.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
      </Campo>
      <Campo label="Evaluaciones incluidas (vacío = ilimitado)">
        <input
          className={s.search}
          type="number"
          min={0}
          placeholder="∞"
          value={creditos}
          disabled={bloqueado}
          title={bloqueado ? tooltip : undefined}
          onChange={(e) => setCreditos(e.target.value)}
        />
      </Campo>
      <label className={s.count} style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
        <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} /> Activo
      </label>
      <div className={s.modalActions}>
        <button className={s.btn} onClick={onCerrar} disabled={ocupado}>
          Cancelar
        </button>
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => void enviar()} disabled={ocupado}>
          {ocupado ? "…" : "Guardar"}
        </button>
      </div>
    </Modal>
  );
}

function VersionarModal({
  plan,
  onHecho,
  onCerrar,
}: {
  plan: ProductoAdmin;
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [nombre, setNombre] = useState(plan.nombre);
  const [descripcion, setDescripcion] = useState(plan.descripcion ?? "");
  const [precio, setPrecio] = useState(String(plan.precio));
  const [moneda, setMoneda] = useState(plan.moneda);
  const [intervalo, setIntervalo] = useState<IntervaloPlan>(plan.intervalo ?? "mensual");
  const [creditos, setCreditos] = useState(plan.evaluacionesIncluidas === null ? "" : String(plan.evaluacionesIncluidas));
  const [desactivarOriginal, setDesactivarOriginal] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function enviar() {
    setError(null);
    if (!nombre.trim()) return setError("El nombre es obligatorio.");
    if (!(Number(precio) >= 0)) return setError("Precio inválido.");
    setOcupado(true);
    try {
      await adminApi.duplicarPlan(plan.id, {
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || null,
        precio: Number(precio),
        moneda: moneda.trim() || "PEN",
        intervalo,
        evaluacionesIncluidas: parseCreditos(creditos),
        activo: true,
        desactivarOriginal,
      });
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Modal titulo={`Nueva versión de · ${plan.nombre}`} onCerrar={onCerrar}>
      <div className={`${s.banner} ${s.bannerInfo}`}>
        Se crea un plan nuevo (el histórico conserva su precio). Ajusta el precio y, si quieres,
        desactiva el original para que deje de ofrecerse.
      </div>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <Campo label="Nombre">
        <input className={s.search} style={{ width: "100%" }} value={nombre} onChange={(e) => setNombre(e.target.value)} />
      </Campo>
      <Campo label="Descripción">
        <input className={s.search} style={{ width: "100%" }} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </Campo>
      <Campo label="Precio">
        <input className={s.search} type="number" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} />
      </Campo>
      <Campo label="Moneda">
        <input className={s.search} maxLength={3} value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} />
      </Campo>
      <Campo label="Intervalo">
        <select className={s.select} value={intervalo} onChange={(e) => setIntervalo(e.target.value as IntervaloPlan)}>
          {INTERVALOS.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
      </Campo>
      <Campo label="Evaluaciones incluidas (vacío = ilimitado)">
        <input
          className={s.search}
          type="number"
          min={0}
          placeholder="∞"
          value={creditos}
          onChange={(e) => setCreditos(e.target.value)}
        />
      </Campo>
      <label className={s.count} style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
        <input
          type="checkbox"
          checked={desactivarOriginal}
          onChange={(e) => setDesactivarOriginal(e.target.checked)}
        />
        Desactivar el plan original
      </label>
      <div className={s.modalActions}>
        <button className={s.btn} onClick={onCerrar} disabled={ocupado}>
          Cancelar
        </button>
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => void enviar()} disabled={ocupado}>
          {ocupado ? "…" : "Crear versión"}
        </button>
      </div>
    </Modal>
  );
}
